import React from 'react';
import { Card, CardContent, Typography, Box } from '@mui/material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { format } from 'date-fns';
import { useArtistHistory } from '../hooks/useArtistMetrics';

interface MomentumChartProps {
    artistId: string;
}

export const ArtistMomentumChart: React.FC<MomentumChartProps> = ({ artistId }) => {
    const { history, loading, error } = useArtistHistory(artistId, 10);

    if (loading) {
        return (
            <Card>
                <CardContent>
                    <Typography variant="h6">Growth Momentum</Typography>
                    <Box display="flex" justifyContent="center" py={4}>
                        <Typography variant="body2" color="textSecondary">Loading momentum data...</Typography>
                    </Box>
                </CardContent>
            </Card>
        );
    }

    if (error || !history || history.length < 2) {
        return (
            <Card>
                <CardContent>
                    <Typography variant="h6">Growth Momentum</Typography>
                    <Box display="flex" justifyContent="center" py={4}>
                        <Typography variant="body2" color="textSecondary">
                            {error ? `Error: ${error}` : 'Not enough data for momentum analysis'}
                        </Typography>
                    </Box>
                </CardContent>
            </Card>
        );
    }

    // Sort by date (oldest first for percentage calculation)
    const sortedHistory = [...history].sort((a, b) => 
        new Date(a.snapshot_date).getTime() - new Date(b.snapshot_date).getTime()
    );

    // Get baseline (oldest) values
    const baseline = sortedHistory[0];
    
    // Calculate percentage changes from baseline
    const chartData = sortedHistory.map((snapshot) => {
        const calculatePercentChange = (current: number | null, base: number | null): number => {
            if (!current || !base || base === 0) return 0;
            return ((current - base) / base) * 100;
        };

        console.log('📊 CHART DEBUG:');
        console.log('📊 Chart Artist ID:', artistId);
        console.log('📊 Chart History Data:', sortedHistory);
        console.log('📊 Latest Chart Data:', sortedHistory[sortedHistory.length - 1]);
        console.log('📊 Baseline Chart Data:', baseline);

        return {
            date: format(new Date(snapshot.snapshot_date), 'MMM dd'),
            fullDate: snapshot.snapshot_date.toLocaleDateString(),
            'Spotify Listeners': calculatePercentChange(
                snapshot.spotify_monthly_listeners, 
                baseline.spotify_monthly_listeners
            ),
            'Instagram': calculatePercentChange(
                snapshot.instagram_followers, 
                baseline.instagram_followers
            ),
            'TikTok': calculatePercentChange(
                snapshot.tiktok_followers, 
                baseline.tiktok_followers
            ),
            'YouTube': calculatePercentChange(
                snapshot.youtube_subscribers, 
                baseline.youtube_subscribers
            ),
            'Shazam Tags': calculatePercentChange(
                snapshot.shazam_count, 
                baseline.shazam_count
            )
        };
    });

    const CustomTooltip = ({ active, payload, label }: any) => {
        if (active && payload && payload.length) {
            return (
                <div style={{ 
                    backgroundColor: 'white', 
                    padding: '10px', 
                    border: '1px solid #ccc',
                    borderRadius: '4px'
                }}>
                    <Typography variant="body2" fontWeight="bold">{label}</Typography>
                    {payload.map((entry: any, index: number) => (
                        <Typography 
                            key={index}
                            variant="caption" 
                            style={{ color: entry.color }}
                            display="block"
                        >
                            {entry.dataKey}: {entry.value > 0 ? '+' : ''}{entry.value.toFixed(1)}%
                        </Typography>
                    ))}
                </div>
            );
        }
        return null;
    };

    return (
        <Card>
            <CardContent>
                <Typography variant="h6" gutterBottom>
                    Growth Momentum (% Change from {format(new Date(baseline.snapshot_date), 'MMM dd')})
                </Typography>
                <Box height={300}>
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="date" />
                            <YAxis 
                                label={{ value: '% Change', angle: -90, position: 'insideLeft' }}
                                tickFormatter={(value) => `${value}%`}
                            />
                            <Tooltip content={<CustomTooltip />} />
                            <Legend />
                            <Line 
                                type="monotone" 
                                dataKey="Spotify Listeners" 
                                stroke="#1DB954" 
                                strokeWidth={3}
                                dot={{ r: 4 }}
                            />
                            <Line 
                                type="monotone" 
                                dataKey="Instagram" 
                                stroke="#E4405F" 
                                strokeWidth={2}
                                dot={{ r: 3 }}
                            />
                            <Line 
                                type="monotone" 
                                dataKey="TikTok" 
                                stroke="#FF0050" 
                                strokeWidth={2}
                                dot={{ r: 3 }}
                            />
                            <Line 
                                type="monotone" 
                                dataKey="YouTube" 
                                stroke="#FF0000" 
                                strokeWidth={2}
                                dot={{ r: 3 }}
                            />
                            <Line 
                                type="monotone" 
                                dataKey="Shazam Tags" 
                                stroke="#0088FF" 
                                strokeWidth={2}
                                dot={{ r: 3 }}
                                strokeDasharray="5 5"
                            />
                        </LineChart>
                    </ResponsiveContainer>
                </Box>
            </CardContent>
        </Card>
    );
};